import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Hands } from "@mediapipe/hands";
import { Camera } from "@mediapipe/camera_utils";
import Matter from "matter-js";
import * as dat from "dat.gui";
import ReceiptPrinter from "./ReceiptPrinter";
import "./CameraPage.scss";

// Model configurations: energy per word and API model mapping
const MODEL_CONFIG = {
  "gpt-4o-mini": {
    energyPerWord: 1.25,
    apiModel: "gpt-4o-mini",
    displayName: "GPT-4o-mini",
  },
  "gpt-5-mini": {
    energyPerWord: 6.5,
    apiModel: "gpt-4o-mini", // Use gpt-4o-mini as proxy for fictional model
    displayName: "GPT-5-mini",
  },
  "gpt-4o": {
    energyPerWord: 12.2,
    apiModel: "gpt-4o",
    displayName: "GPT-4o",
  },
  "gpt-5-pro": {
    energyPerWord: 132.1,
    apiModel: "gpt-4o", // Use gpt-4o as proxy for fictional model
    displayName: "GPT-5-pro",
  },
};

// Wrap text to receipt-width lines (~35 chars per line)
const wrapText = (text, maxWidth = 35) => {
  const paragraphs = text.split("\n");
  const wrappedLines = [];

  paragraphs.forEach((paragraph) => {
    if (paragraph.trim() === "") {
      wrappedLines.push(""); // Preserve empty lines between paragraphs
      return;
    }

    const words = paragraph.split(/\s+/);
    let currentLine = "";

    words.forEach((word) => {
      if (currentLine.length + word.length + 1 <= maxWidth) {
        currentLine += (currentLine ? " " : "") + word;
      } else {
        if (currentLine) wrappedLines.push(currentLine);
        currentLine = word;
      }
    });

    if (currentLine) wrappedLines.push(currentLine);
  });

  return wrappedLines;
};

// Helper to compute energy thresholds from text
const computeEnergyThresholds = (text, energyPerWord) => {
  const lines = wrapText(text);
  let cumulative = 0;
  const thresholds = lines.map((line) => {
    const wordCount = line.trim().split(/\s+/).filter(Boolean).length;
    const lineCost = Math.max(wordCount, 1) * energyPerWord; // Energy per word based on model
    cumulative += lineCost;
    return cumulative;
  });
  return { lines, thresholds };
};

// Hand joints matching the 15 shown landmarks: 0, 2, 4, 5, 6, 8, 9, 10, 12, 13, 14, 16, 17, 18, 20
const handJoints = [
  // wrist (landmark 0)
  { open: [0, 40], fist: [0, 40] },
  // thumb MCP (landmark 2)
  { open: [-22, 18], fist: [-18, 18] },
  // thumb TIP (landmark 4)
  { open: [-35, -8], fist: [-5, 25] },
  // index MCP (landmark 5)
  { open: [-10, 5], fist: [-10, 5] },
  // index PIP (landmark 6)
  { open: [-12, -18], fist: [-6, 12] },
  // index TIP (landmark 8)
  { open: [-12, -38], fist: [0, 25] },
  // middle MCP (landmark 9)
  { open: [0, 2], fist: [0, 2] },
  // middle PIP (landmark 10)
  { open: [0, -22], fist: [0, 10] },
  // middle TIP (landmark 12)
  { open: [0, -42], fist: [5, 25] },
  // ring MCP (landmark 13)
  { open: [10, 5], fist: [10, 5] },
  // ring PIP (landmark 14)
  { open: [12, -18], fist: [6, 12] },
  // ring TIP (landmark 16)
  { open: [12, -38], fist: [0, 25] },
  // pinky MCP (landmark 17)
  { open: [20, 10], fist: [20, 10] },
  // pinky PIP (landmark 18)
  { open: [25, -8], fist: [15, 14] },
  // pinky TIP (landmark 20)
  { open: [28, -25], fist: [5, 25] },
];

function CameraPage() {
  const location = useLocation();
  const question = location.state?.question || "";
  const selectedModel = location.state?.model || "gpt-4o";
  const modelConfig = MODEL_CONFIG[selectedModel] || MODEL_CONFIG["gpt-4o"];

  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [nudgeButton, setNudgeButton] = useState(false);
  const [error, setError] = useState(null);
  const [stream, setStream] = useState(null);
  const [isFist, setIsFist] = useState(false);
  const [isHandInView, setIsHandInView] = useState(false);
  const [visibleLines, setVisibleLines] = useState(0);
  const [numberOfCranks, setNumberOfCranks] = useState(0);
  const [crankingTime, setCrankingTime] = useState("00:00:00");
  const [energyGenerated, setEnergyGenerated] = useState("0.00 J");
  const [crankProgress, setCrankProgress] = useState(0);
  const [hasCrankedEnough, setHasCrankedEnough] = useState(false);
  const [responseText, setResponseText] = useState("");
  const [isLoadingAnswer, setIsLoadingAnswer] = useState(false);
  const crankEnergyRef = useRef(0); // Accumulated energy in joules
  const crankElapsedRef = useRef(0); // Accumulated cranking seconds
  const crankFistStartRef = useRef(null); // Timestamp when current fist session started
  const crankTimerRef = useRef(null); // Interval ID for cranking timer
  const fistLostTimerRef = useRef(null); // For fist tracking grace period
  const visibleLinesRef = useRef(0);
  const lastFistAngleRef = useRef(null);
  const smoothedAngleRef = useRef(null);
  const rotationAngleRef = useRef(0);
  const cameraEnabledRef = useRef(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const handsRef = useRef(null);
  const cameraRef = useRef(null);
  const animationRef = useRef(null);
  const orbitCircleRef = useRef(null);
  const physicsCanvasRef = useRef(null);
  const engineRef = useRef(null);
  const wordsRef = useRef([]);
  const wallsRef = useRef([]);
  const circleCenterRef = useRef({ x: 0, y: 0 });
  const guiRef = useRef(null);
  const physicsParamsRef = useRef({
    gravity: 1,
    rotationSpeed: 20,
    restitution: 0.6,
    friction: 0.3,
    density: 0.0002,
    frictionAir: 0.005,
    crankSmoothing: 0.2,
  });
  const debugRef = useRef({
    showStrokes: false,
  });
  const onResultsRef = useRef(null);

  // Default response text for energy calculation initialization
  const defaultResponseText = "Loading your answer...";

  // Response lines for energy calculation (initialized with default)
  const responseLines = useRef(
    computeEnergyThresholds(defaultResponseText, modelConfig.energyPerWord)
      .lines
  );

  // Cumulative energy needed to reveal up to line N (initialized with default)
  const lineEnergyCumulative = useRef(
    computeEnergyThresholds(defaultResponseText, modelConfig.energyPerWord)
      .thresholds
  );

  // Helper to update energy thresholds when response arrives
  const computeLineEnergy = useCallback(
    (text) => {
      const { lines, thresholds } = computeEnergyThresholds(
        text,
        modelConfig.energyPerWord
      );
      responseLines.current = lines;
      lineEnergyCumulative.current = thresholds;
      // Store wrapped text for ReceiptPrinter (joined with \n so .split("\n") works)
      setResponseText(lines.join("\n"));
      // Reset progress when new response arrives
      crankEnergyRef.current = 0;
      visibleLinesRef.current = 0;
      setVisibleLines(0);
      setCrankProgress(0);
      setEnergyGenerated("0.00 J");
      setHasCrankedEnough(false);
    },
    [modelConfig.energyPerWord]
  );

  // Fetch answer from OpenAI API
  useEffect(() => {
    if (!question) return;

    const fetchAnswer = async () => {
      setIsLoadingAnswer(true);
      console.log("Loading answer...");
      try {
        const response = await fetch("/api/chat", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: modelConfig.apiModel,
            messages: [
              {
                role: "system",
                content:
                  "You are a helpful assistant. Keep responses concise and well-formatted. Use line breaks between paragraphs and list items for readability.",
              },
              {
                role: "user",
                content: question,
              },
            ],
            max_tokens: 1000,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch answer");
        }

        const data = await response.json();
        const answerText = data.choices[0]?.message?.content || "";
        console.log("API response:", answerText);
        computeLineEnergy(answerText);
      } catch (err) {
        console.error("Error fetching answer:", err);
        // Use default response on error
        const defaultText =
          "Sorry, I couldn't fetch an answer. Please try again.";
        computeLineEnergy(defaultText);
      } finally {
        setIsLoadingAnswer(false);
      }
    };

    fetchAnswer();
  }, [question, computeLineEnergy, modelConfig.apiModel]);

  // Log loading state for debugging
  useEffect(() => {
    console.log("isLoadingAnswer:", isLoadingAnswer);
  }, [isLoadingAnswer]);

  const formatTime = (totalSeconds) => {
    const hrs = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
    const mins = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
      2,
      "0"
    );
    const secs = String(Math.floor(totalSeconds % 60)).padStart(2, "0");
    return `${hrs}:${mins}:${secs}`;
  };

  const updateOrbitTransform = useCallback(() => {
    if (!orbitCircleRef.current) return;

    const normalizedAngle = ((rotationAngleRef.current % 360) + 360) % 360;

    orbitCircleRef.current.setAttribute(
      "transform",
      `rotate(${normalizedAngle}, 200, 200)`
    );
  }, []);

  useEffect(() => {
    if (cameraEnabled) return;
    const t = setTimeout(() => setNudgeButton(true), 5000);
    return () => clearTimeout(t);
  }, [cameraEnabled]);

  const handleEnableCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });

      console.log("Camera stream obtained:", mediaStream);
      setStream(mediaStream);
      setCameraEnabled(true);
      cameraEnabledRef.current = true;
      setError(null);
    } catch (err) {
      console.error("Camera access denied:", err);
      setError("Camera access denied. Please allow camera access to continue.");
    }
  };

  useEffect(() => {
    if (stream && videoRef.current) {
      console.log("Setting video srcObject");
      videoRef.current.srcObject = stream;
    }

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  // Pause/resume animation when camera is enabled/disabled
  useEffect(() => {
    if (!animationRef.current) return;

    if (cameraEnabled) {
      const svg = animationRef.current;
      const rotationDuration = 18; // seconds for a full rotation
      const degreesPerSecond = 360 / rotationDuration;
      const currentTime = svg.getCurrentTime ? svg.getCurrentTime() : 0;
      rotationAngleRef.current =
        ((currentTime % rotationDuration) * degreesPerSecond) % 360;
      updateOrbitTransform();
    } else if (orbitCircleRef.current) {
      orbitCircleRef.current.removeAttribute("transform");
    }
  }, [cameraEnabled, updateOrbitTransform]);

  // Initialize dat.GUI
  useEffect(() => {
    if (!guiRef.current) {
      const gui = new dat.GUI();
      guiRef.current = gui;

      const physicsFolder = gui.addFolder("Physics Parameters");
      physicsFolder.close();

      physicsFolder
        .add(physicsParamsRef.current, "gravity", 0, 5)
        .onChange((value) => {
          if (engineRef.current) {
            engineRef.current.gravity.y = value;
          }
        });

      physicsFolder
        .add(physicsParamsRef.current, "rotationSpeed", 0, 100)
        .name("Rotation Speed (deg/s)");

      physicsFolder
        .add(physicsParamsRef.current, "restitution", 0, 1)
        .name("Bounciness")
        .onChange((value) => {
          wordsRef.current.forEach(({ body }) => {
            body.restitution = value;
          });
        });

      physicsFolder
        .add(physicsParamsRef.current, "friction", 0, 1)
        .onChange((value) => {
          wordsRef.current.forEach(({ body }) => {
            body.friction = value;
          });
        });

      physicsFolder
        .add(physicsParamsRef.current, "density", 0, 0.01)
        .onChange((value) => {
          const Body = Matter.Body;
          wordsRef.current.forEach(({ body }) => {
            Body.setDensity(body, value);
          });
        });

      physicsFolder
        .add(physicsParamsRef.current, "frictionAir", 0, 0.1)
        .name("Air Resistance")
        .onChange((value) => {
          wordsRef.current.forEach(({ body }) => {
            body.frictionAir = value;
          });
        });

      const controlFolder = gui.addFolder("Controls");
      controlFolder
        .add(physicsParamsRef.current, "crankSmoothing", 0, 0.2)
        .name("Crank Smoothing")
        .onChange(() => {
          // Reset smoothed angle when changing smoothing factor
          smoothedAngleRef.current = null;
        });
      controlFolder.open();

      const debugFolder = gui.addFolder("Debug Strokes");
      debugFolder.add(debugRef.current, "showStrokes").name("Show");
      debugFolder.open();

      // Hide the GUI by default
      gui.hide();
    }

    return () => {
      if (guiRef.current) {
        guiRef.current.destroy();
        guiRef.current = null;
      }
    };
  }, []);

  // Initialize Matter.js physics for falling text
  useEffect(() => {
    if (!physicsCanvasRef.current || !question || !animationRef.current) return;

    const Engine = Matter.Engine;
    const World = Matter.World;
    const Bodies = Matter.Bodies;
    const Runner = Matter.Runner;
    const Events = Matter.Events;

    const canvas = physicsCanvasRef.current;
    const svgRect = animationRef.current.getBoundingClientRect();

    const width = svgRect.width;
    const height = svgRect.height;

    // Set canvas resolution for high DPI displays
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = (180 / 400) * width; // Scale radius to actual SVG size

    // Store circle center for use in hand tracking
    circleCenterRef.current = { x: centerX, y: centerY };

    // Create engine with initial gravity from params
    const engine = Engine.create({
      gravity: { x: 0, y: physicsParamsRef.current.gravity },
    });
    engineRef.current = engine;

    // Create circular boundary
    const segments = 32;
    const walls = [];
    const obstacles = [];

    for (let i = 0; i < segments; i++) {
      const angle1 = (i / segments) * Math.PI * 2;
      const angle2 = ((i + 1) / segments) * Math.PI * 2;
      const x1 = centerX + radius * Math.cos(angle1);
      const y1 = centerY + radius * Math.sin(angle1);
      const x2 = centerX + radius * Math.cos(angle2);
      const y2 = centerY + radius * Math.sin(angle2);

      const wall = Bodies.rectangle(
        (x1 + x2) / 2,
        (y1 + y2) / 2,
        Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2),
        10,
        {
          isStatic: true,
          angle: Math.atan2(y2 - y1, x2 - x1),
        }
      );
      walls.push(wall);

      // Add obstacles on inner side of wall every 4 segments
      if (i % 4 === 0) {
        const midAngle = (angle1 + angle2) / 2;
        const obstacleRadius = radius - 5; // Position inside the wall
        const obstacleX = centerX + obstacleRadius * Math.cos(midAngle);
        const obstacleY = centerY + obstacleRadius * Math.sin(midAngle);

        const obstacle = Bodies.rectangle(obstacleX, obstacleY, 50, 15, {
          isStatic: true,
          angle: midAngle,
        });
        obstacles.push(obstacle);
        walls.push(obstacle); // Add to walls array so it rotates too
      }
    }

    // Create word bodies from question using initial params
    const words = question.split(" ");
    const wordBodies = words.map((word, index) => {
      const x = centerX + (Math.random() - 0.5) * radius * 0.8;
      const y = 50 + index * 40;
      const wordWidth = word.length * 17;

      return {
        body: Bodies.rectangle(x, y, wordWidth, 36, {
          restitution: physicsParamsRef.current.restitution,
          friction: physicsParamsRef.current.friction,
          density: physicsParamsRef.current.density,
          frictionAir: physicsParamsRef.current.frictionAir,
        }),
        text: word,
      };
    });

    wordsRef.current = wordBodies;
    wallsRef.current = walls; // Store walls for access from hand tracking
    World.add(engine.world, [...walls, ...wordBodies.map((w) => w.body)]);

    // Rotate walls continuously using rotation speed from params (only when camera is off)
    const Body = Matter.Body;

    Events.on(engine, "beforeUpdate", () => {
      // Only rotate walls when camera is not enabled
      if (!cameraEnabledRef.current) {
        const rotationSpeed =
          (physicsParamsRef.current.rotationSpeed * Math.PI) / 180;
        const deltaTime = engine.timing.lastDelta / 1000; // Convert to seconds
        walls.forEach((wall) => {
          Body.rotate(wall, rotationSpeed * deltaTime, {
            x: centerX,
            y: centerY,
          });
        });
      }
    });

    // Custom render loop
    const renderText = () => {
      ctx.clearRect(0, 0, width, height);

      // Draw debug strokes if enabled
      if (debugRef.current.showStrokes) {
        // Draw the physics boundary circle in red
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        ctx.strokeStyle = "red";
        ctx.lineWidth = 1;
        ctx.stroke();

        // Draw obstacles with green stroke
        obstacles.forEach((obstacle) => {
          const vertices = obstacle.vertices;
          ctx.beginPath();
          ctx.moveTo(vertices[0].x, vertices[0].y);
          for (let i = 1; i < vertices.length; i++) {
            ctx.lineTo(vertices[i].x, vertices[i].y);
          }
          ctx.closePath();
          ctx.strokeStyle = "green";
          ctx.lineWidth = 2;
          ctx.stroke();
        });
      }

      // Draw words
      ctx.font = '28px "JetBrains Mono", monospace';
      ctx.fillStyle = "white";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      wordsRef.current.forEach(({ body, text }) => {
        // Draw the physics rectangle with yellow stroke if debug is enabled
        if (debugRef.current.showStrokes) {
          const vertices = body.vertices;
          ctx.beginPath();
          ctx.moveTo(vertices[0].x, vertices[0].y);
          for (let i = 1; i < vertices.length; i++) {
            ctx.lineTo(vertices[i].x, vertices[i].y);
          }
          ctx.closePath();
          ctx.strokeStyle = "yellow";
          ctx.lineWidth = 1;
          ctx.stroke();
        }

        // Draw text with rotation matching the physics body
        ctx.save();
        ctx.translate(body.position.x, body.position.y);
        ctx.rotate(body.angle);
        ctx.fillText(text, 0, 0);
        ctx.restore();
      });

      requestAnimationFrame(renderText);
    };
    renderText();

    const runner = Runner.create();
    Runner.run(runner, engine);

    return () => {
      Runner.stop(runner);
      World.clear(engine.world);
      Engine.clear(engine);
    };
  }, [question]);

  // Helper functions for hand tracking (defined before useEffect that uses them)
  const detectFist = (landmarks) => {
    // Check if all fingertips (4, 8, 12, 16, 20) are below their respective knuckles
    const indexTip = landmarks[8];
    const indexMcp = landmarks[5];
    const middleTip = landmarks[12];
    const middleMcp = landmarks[9];
    const ringTip = landmarks[16];
    const ringMcp = landmarks[13];
    const pinkyTip = landmarks[20];
    const pinkyMcp = landmarks[17];

    // For a fist, fingertips should be lower (higher y value) than knuckles
    const indexClosed = indexTip.y > indexMcp.y;
    const middleClosed = middleTip.y > middleMcp.y;
    const ringClosed = ringTip.y > ringMcp.y;
    const pinkyClosed = pinkyTip.y > pinkyMcp.y;

    return indexClosed && middleClosed && ringClosed && pinkyClosed;
  };

  const calculateHandAngle = (landmarks, canvasWidth, canvasHeight) => {
    if (!animationRef.current) return null;

    const wrist = landmarks[0];
    const handX = (1 - wrist.x) * canvasWidth;
    const handY = wrist.y * canvasHeight;

    const svgRect = animationRef.current.getBoundingClientRect();
    const canvasRect = canvasRef.current.getBoundingClientRect();

    const handScreenX =
      canvasRect.left + (handX / canvasWidth) * canvasRect.width;
    const handScreenY =
      canvasRect.top + (handY / canvasHeight) * canvasRect.height;

    const centerX = svgRect.left + svgRect.width / 2;
    const centerY = svgRect.top + svgRect.height / 2;

    const dx = handScreenX - centerX;
    const dy = handScreenY - centerY;
    const radians = Math.atan2(dy, dx);

    let degrees = radians * (180 / Math.PI);
    degrees = degrees + 90;
    if (degrees < 0) degrees += 360;

    return degrees;
  };

  const drawConnectors = (ctx, landmarks, width, height) => {
    const connections = [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 4],
      [0, 5],
      [5, 6],
      [6, 7],
      [7, 8],
      [0, 9],
      [9, 10],
      [10, 11],
      [11, 12],
      [0, 13],
      [13, 14],
      [14, 15],
      [15, 16],
      [0, 17],
      [17, 18],
      [18, 19],
      [19, 20],
      [5, 9],
      [9, 13],
      [13, 17],
    ];

    ctx.strokeStyle = "rgba(55, 111, 225, 0.8)";
    ctx.lineWidth = 2;

    connections.forEach(([start, end]) => {
      const startPoint = landmarks[start];
      const endPoint = landmarks[end];
      ctx.beginPath();
      ctx.moveTo(startPoint.x * width, startPoint.y * height);
      ctx.lineTo(endPoint.x * width, endPoint.y * height);
      ctx.stroke();
    });
  };

  const drawLandmarks = (ctx, landmarks, width, height) => {
    const jointsToShow = [0, 2, 4, 5, 6, 8, 9, 10, 12, 13, 14, 16, 17, 18, 20];

    landmarks.forEach((landmark, index) => {
      if (!jointsToShow.includes(index)) return;

      const x = landmark.x * width;
      const y = landmark.y * height;

      ctx.beginPath();
      ctx.arc(x, y, 8, 0, 2 * Math.PI);
      ctx.fillStyle = "rgba(180, 205, 255, 0.3)";
      ctx.fill();

      ctx.beginPath();
      ctx.arc(x, y, 4, 0, 2 * Math.PI);
      ctx.fillStyle =
        index === 0 ||
        index === 4 ||
        index === 8 ||
        index === 12 ||
        index === 16 ||
        index === 20
          ? "rgb(18, 90, 235)"
          : "rgb(98, 132, 200)";
      ctx.fill();

      ctx.strokeStyle = "rgba(255, 255, 255, 0.8)";
      ctx.lineWidth = 1;
      ctx.stroke();
    });
  };

  const onResults = useCallback(
    (results) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      const dpr = window.devicePixelRatio || 1;

      ctx.save();
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let fistDetected = false;
      let detectedAngle = 0;
      const handInView = !!(
        results.multiHandLandmarks && results.multiHandLandmarks.length > 0
      );

      const logicalWidth = canvas.width / dpr;
      const logicalHeight = canvas.height / dpr;

      if (results.multiHandLandmarks) {
        for (const landmarks of results.multiHandLandmarks) {
          if (detectFist(landmarks)) {
            fistDetected = true;
            detectedAngle = calculateHandAngle(
              landmarks,
              logicalWidth,
              logicalHeight
            );
          }
          drawConnectors(ctx, landmarks, logicalWidth, logicalHeight);
          drawLandmarks(ctx, landmarks, logicalWidth, logicalHeight);
        }
      }

      // If fist is detected, cancel any pending "fist lost" timer
      if (fistDetected) {
        if (fistLostTimerRef.current) {
          clearTimeout(fistLostTimerRef.current);
          fistLostTimerRef.current = null;
        }
      }

      if (fistDetected && detectedAngle !== null) {
        if (smoothedAngleRef.current === null) {
          smoothedAngleRef.current = detectedAngle;
        }

        const smoothingFactor = physicsParamsRef.current.crankSmoothing;

        let angleDiff = detectedAngle - smoothedAngleRef.current;
        if (angleDiff > 180) angleDiff -= 360;
        if (angleDiff < -180) angleDiff += 360;

        smoothedAngleRef.current += angleDiff * smoothingFactor;
        smoothedAngleRef.current =
          ((smoothedAngleRef.current % 360) + 360) % 360;

        if (lastFistAngleRef.current !== null) {
          let angleDelta = smoothedAngleRef.current - lastFistAngleRef.current;

          if (angleDelta > 180) angleDelta -= 360;
          if (angleDelta < -180) angleDelta += 360;

          const maxDeltaPerFrame = 20;
          if (Math.abs(angleDelta) > maxDeltaPerFrame) {
            angleDelta = Math.sign(angleDelta) * maxDeltaPerFrame;
          }

          // Process any movement (not just clockwise)
          const absAngleDelta = Math.abs(angleDelta);
          if (absAngleDelta > 0) {
            rotationAngleRef.current += absAngleDelta;
            updateOrbitTransform();

            // Start/resume cranking timer when fist is active
            if (crankFistStartRef.current === null) {
              crankFistStartRef.current = Date.now();
              if (!crankTimerRef.current) {
                crankTimerRef.current = setInterval(() => {
                  const sessionTime = Math.floor(
                    (Date.now() - crankFistStartRef.current) / 1000
                  );
                  setCrankingTime(
                    formatTime(crankElapsedRef.current + sessionTime)
                  );
                }, 1000);
              }
            }

            // Check if user has cranked enough to hide overlay (1 second)
            if (!hasCrankedEnough && crankFistStartRef.current) {
              const duration = (Date.now() - crankFistStartRef.current) / 1000;
              if (duration > 2.0) {
                setHasCrankedEnough(true);
              }
            }

            // Check if already complete (all lines revealed)
            const thresholds = lineEnergyCumulative.current;
            const isComplete = visibleLinesRef.current >= thresholds.length;

            // Only accumulate energy if not complete
            if (!isComplete) {
              // Accumulate crank energy: 25J per full 360° rotation
              const energyGained = (absAngleDelta / 360) * 25;
              crankEnergyRef.current += energyGained;
              setEnergyGenerated(`${crankEnergyRef.current.toFixed(2)} J`);

              // Update progress percentage
              const totalEnergy = thresholds[thresholds.length - 1] || 1;
              const progressPercent = Math.min(
                100,
                Math.round((crankEnergyRef.current / totalEnergy) * 100)
              );
              setCrankProgress(progressPercent);

              // Count full rotations
              setNumberOfCranks(
                Math.floor(Math.abs(rotationAngleRef.current) / 360)
              );

              // Check if enough energy to reveal next line(s)
              let newLines = visibleLinesRef.current;
              while (
                newLines < thresholds.length &&
                crankEnergyRef.current >= thresholds[newLines]
              ) {
                newLines++;
              }
              if (newLines !== visibleLinesRef.current) {
                visibleLinesRef.current = newLines;
                setVisibleLines(newLines);

                // Stop timer if just completed
                if (newLines >= thresholds.length && crankTimerRef.current) {
                  clearInterval(crankTimerRef.current);
                  crankTimerRef.current = null;
                  // Finalize cranking time
                  if (crankFistStartRef.current !== null) {
                    crankElapsedRef.current += Math.floor(
                      (Date.now() - crankFistStartRef.current) / 1000
                    );
                    crankFistStartRef.current = null;
                    setCrankingTime(formatTime(crankElapsedRef.current));
                  }
                }
              }
            }

            if (wallsRef.current.length > 0 && cameraEnabledRef.current) {
              const Body = Matter.Body;
              const angleRadians = (absAngleDelta * Math.PI) / 180;
              const center = circleCenterRef.current;

              wallsRef.current.forEach((wall) => {
                Body.rotate(wall, angleRadians, { x: center.x, y: center.y });
              });
            }
          }
        }
        lastFistAngleRef.current = smoothedAngleRef.current;
      } else {
        // Pause cranking timer when fist is released
        if (crankFistStartRef.current !== null) {
          crankElapsedRef.current += Math.floor(
            (Date.now() - crankFistStartRef.current) / 1000
          );
          crankFistStartRef.current = null;
          if (crankTimerRef.current) {
            clearInterval(crankTimerRef.current);
            crankTimerRef.current = null;
          }
          setCrankingTime(formatTime(crankElapsedRef.current));
        }
        // Don't reset angle immediately. Start a timer for a grace period.
        if (lastFistAngleRef.current !== null && !fistLostTimerRef.current) {
          fistLostTimerRef.current = setTimeout(() => {
            lastFistAngleRef.current = null;
            smoothedAngleRef.current = null;
            fistLostTimerRef.current = null; // Clear self
            setHasCrankedEnough(false);
          }, 500); // 500ms grace period before resetting angle
        }
      }

      setIsHandInView(handInView);
      setIsFist(fistDetected);
      ctx.restore();
    },
    [updateOrbitTransform, hasCrankedEnough]
  );

  // Keep onResultsRef updated to avoid re-initializing MediaPipe when onResults changes
  useEffect(() => {
    onResultsRef.current = onResults;
  }, [onResults]);

  // Initialize MediaPipe Hands
  useEffect(() => {
    if (!cameraEnabled || !videoRef.current || !canvasRef.current) return;

    const hands = new Hands({
      locateFile: (file) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`;
      },
    });

    hands.setOptions({
      maxNumHands: 1,
      modelComplexity: 1,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });

    hands.onResults((results) => {
      if (onResultsRef.current) {
        onResultsRef.current(results);
      }
    });
    handsRef.current = hands;

    if (videoRef.current) {
      // Update canvas size to match video dimensions with proper DPI scaling
      const updateCanvasSize = () => {
        if (videoRef.current && canvasRef.current) {
          const video = videoRef.current;
          const dpr = window.devicePixelRatio || 1;
          canvasRef.current.width = (video.videoWidth || 640) * dpr;
          canvasRef.current.height = (video.videoHeight || 480) * dpr;
          const ctx = canvasRef.current.getContext("2d");
          ctx.scale(dpr, dpr);
        }
      };

      videoRef.current.addEventListener("loadedmetadata", updateCanvasSize);

      const camera = new Camera(videoRef.current, {
        onFrame: async () => {
          if (videoRef.current) {
            await hands.send({ image: videoRef.current });
          }
        },
        width: 640,
        height: 480,
      });
      camera.start();
      cameraRef.current = camera;
    }

    return () => {
      if (cameraRef.current) {
        cameraRef.current.stop();
      }
    };
  }, [cameraEnabled]);

  return (
    <div className="CameraPage">
      <div className="camera-content">
        <div className="camera-left-section">
          <div className="camera-text-stack">
            <div
              className={`camera-left-text${
                cameraEnabled ? " camera-text-exit" : ""
              }`}
            >
              <h1 className="camera-title">Time to embody your question</h1>
              <div className="camera-text-body">
                <p className="camera-subtitle">
                  Please grant camera access to get your answer.
                </p>

                <button
                  className={`enable-camera-button${
                    nudgeButton ? " nudge" : ""
                  }`}
                  onClick={handleEnableCamera}
                  disabled={cameraEnabled}
                >
                  Enable Camera
                </button>

                {error && <p className="camera-error">{error}</p>}
              </div>
            </div>

            {cameraEnabled && (
              <div className="camera-instruction-text">
                <h1 className="camera-title">Make a fist and start cranking</h1>
                <p className="camera-subtitle">
                  Your body powers the answer. Crank to generate energy and
                  reveal your response.
                </p>
              </div>
            )}
          </div>

          {cameraEnabled && (
            <ReceiptPrinter
              modelUsed={modelConfig.displayName}
              numberOfCranks={numberOfCranks}
              crankingTime={crankingTime}
              energyGenerated={energyGenerated}
              showReceipt={true}
              visibleLines={visibleLines}
              question={question}
              responseText={responseText}
            />
          )}
        </div>

        <div className="camera-right-section">
          {cameraEnabled && (
            <div className="camera-video-wrapper">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="camera-video"
              />
            </div>
          )}
          {cameraEnabled && <div className="right-overlay"></div>}
          {cameraEnabled && <canvas ref={canvasRef} className="hand-canvas" />}
          <div className="circle-container">
            <div className="circle-wrapper" style={{ position: "relative" }}>
              <canvas
                ref={physicsCanvasRef}
                className="physics-canvas"
                style={{ position: "absolute", top: 0, left: 0, zIndex: 0 }}
              />
              <svg
                ref={animationRef}
                className="circle-svg"
                viewBox="0 0 400 400"
                style={{ position: "relative", zIndex: 10 }}
              >
                <circle
                  cx="200"
                  cy="200"
                  r="180"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                />
                <text
                  x="200"
                  y="200"
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="white"
                  opacity="0.5"
                  fontSize="32"
                  fontFamily="'JetBrains Mono', monospace"
                >
                  {crankProgress}%
                </text>
                <circle
                  ref={orbitCircleRef}
                  className="orbiting-circle"
                  cx="200"
                  cy="20"
                  r="15"
                  fill={isFist ? "white" : "none"}
                  stroke="white"
                  strokeWidth="2"
                >
                  {!cameraEnabled && (
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      from="0 200 200"
                      to="360 200 200"
                      dur="18s"
                      repeatCount="indefinite"
                    />
                  )}
                </circle>
                {/* Darken overlay for instructions */}
                {cameraEnabled && (
                  <circle
                    cx="200"
                    cy="200"
                    r="180"
                    fill="rgba(0,0,0,0.5)"
                    style={{
                      opacity: hasCrankedEnough || crankProgress >= 1 ? 0 : 1,
                      transition: "opacity 0.5s ease-in-out",
                      pointerEvents: "none",
                    }}
                  />
                )}
                {/* Instructional Indicators - always rendered, visibility controlled by opacity */}
                {cameraEnabled && (
                  <g
                    className="instructional-indicator"
                    style={{
                      opacity: crankProgress >= 100 || hasCrankedEnough ? 0 : 1,
                      transition: "opacity 0.5s ease-in-out",
                      pointerEvents:
                        crankProgress >= 100 || hasCrankedEnough
                          ? "none"
                          : "auto",
                    }}
                  >
                    {/* 1. Indicator to show hand */}
                    <g
                      style={{
                        opacity: !isHandInView ? 1 : 0,
                        transition: "opacity 1s ease-in-out",
                      }}
                    >
                      <g>
                        <animateTransform
                          attributeName="transform"
                          type="translate"
                          values="0 0; 0 -10; 0 0"
                          dur="2s"
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="opacity"
                          values="0.7; 1; 0.7"
                          dur="2s"
                          repeatCount="indefinite"
                        />
                        <g transform="translate(200, 200)">
                          <path
                            d="M 0 40 L -22 18 L -35 -8 M 0 40 L -10 5 L -12 -18 L -12 -38 M 0 40 L 0 2 L 0 -22 L 0 -42 M 0 40 L 10 5 L 12 -18 L 12 -38 M 0 40 L 20 10 L 25 -8 L 28 -25 M -10 5 L 0 2 L 10 5 L 20 10"
                            fill="none"
                            stroke="#4da6ff"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          {handJoints.map(({ open }, i) => (
                            <circle
                              key={i}
                              cx={open[0]}
                              cy={open[1]}
                              r="3"
                              fill={
                                [2, 5, 8, 11, 14].includes(i)
                                  ? "white"
                                  : "#4da6ff"
                              }
                            />
                          ))}
                        </g>
                      </g>
                      <text
                        x="200"
                        y="255"
                        textAnchor="middle"
                        fill="white"
                        fontSize="16"
                        fontFamily="'JetBrains Mono', monospace"
                        style={{ pointerEvents: "none" }}
                      >
                        Show your hand
                      </text>
                    </g>

                    {/* 2. Indicator to make a fist */}
                    <g
                      style={{
                        opacity: isHandInView && !isFist ? 1 : 0,
                        transition: "opacity 1s ease-in-out",
                      }}
                    >
                      <g transform="translate(200, 200)">
                        <path
                          d="M 0 40 L -22 18 L -35 -8 M 0 40 L -10 5 L -12 -18 L -12 -38 M 0 40 L 0 2 L 0 -22 L 0 -42 M 0 40 L 10 5 L 12 -18 L 12 -38 M 0 40 L 20 10 L 25 -8 L 28 -25 M -10 5 L 0 2 L 10 5 L 20 10"
                          fill="none"
                          stroke="#4da6ff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <animate
                            attributeName="d"
                            values="M 0 40 L -22 18 L -35 -8 M 0 40 L -10 5 L -12 -18 L -12 -38 M 0 40 L 0 2 L 0 -22 L 0 -42 M 0 40 L 10 5 L 12 -18 L 12 -38 M 0 40 L 20 10 L 25 -8 L 28 -25 M -10 5 L 0 2 L 10 5 L 20 10;M 0 40 L -18 18 L -5 25 M 0 40 L -10 5 L -6 12 L 0 25 M 0 40 L 0 2 L 0 10 L 5 25 M 0 40 L 10 5 L 6 12 L 0 25 M 0 40 L 20 10 L 15 14 L 5 25 M -10 5 L 0 2 L 10 5 L 20 10;M 0 40 L -18 18 L -5 25 M 0 40 L -10 5 L -6 12 L 0 25 M 0 40 L 0 2 L 0 10 L 5 25 M 0 40 L 10 5 L 6 12 L 0 25 M 0 40 L 20 10 L 15 14 L 5 25 M -10 5 L 0 2 L 10 5 L 20 10;M 0 40 L -22 18 L -35 -8 M 0 40 L -10 5 L -12 -18 L -12 -38 M 0 40 L 0 2 L 0 -22 L 0 -42 M 0 40 L 10 5 L 12 -18 L 12 -38 M 0 40 L 20 10 L 25 -8 L 28 -25 M -10 5 L 0 2 L 10 5 L 20 10"
                            keyTimes="0;0.4;0.7;1"
                            dur="2s"
                            repeatCount="indefinite"
                          />
                        </path>
                        {handJoints.map(({ open, fist }, i) => (
                          <circle
                            key={i}
                            r="3"
                            fill={
                              [2, 5, 8, 11, 14].includes(i)
                                ? "white"
                                : "#4da6ff"
                            }
                          >
                            <animate
                              attributeName="cx"
                              values={`${open[0]};${fist[0]};${fist[0]};${open[0]}`}
                              keyTimes="0;0.4;0.7;1"
                              dur="2s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="cy"
                              values={`${open[1]};${fist[1]};${fist[1]};${open[1]}`}
                              keyTimes="0;0.4;0.7;1"
                              dur="2s"
                              repeatCount="indefinite"
                            />
                          </circle>
                        ))}
                      </g>
                      <text
                        x="200"
                        y="255"
                        textAnchor="middle"
                        fill="white"
                        fontSize="16"
                        fontFamily="'JetBrains Mono', monospace"
                        style={{ pointerEvents: "none" }}
                      >
                        Make a fist
                      </text>
                    </g>

                    {/* 3. Indicator to crank */}
                    <g
                      className="clockwise-indicator"
                      style={{
                        opacity: isFist ? 1 : 0,
                        transition: "opacity 1s ease-in-out",
                      }}
                    >
                      <defs>
                        <marker
                          id="arrowhead"
                          markerWidth="10"
                          markerHeight="7"
                          refX="9"
                          refY="3.5"
                          orient="auto"
                        >
                          <polygon
                            points="0 0, 10 3.5, 0 7"
                            fill="white"
                            opacity="1"
                          />
                        </marker>
                      </defs>
                      <path
                        d="M 320 15 A 220 220 0 0 1 385 85"
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                        opacity="0.5"
                        markerEnd="url(#arrowhead)"
                      >
                        <animateTransform
                          attributeName="transform"
                          type="rotate"
                          values="-5 200 200; 5 200 200; -5 200 200"
                          dur="4s"
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="opacity"
                          values="0.3;0.8;0.3"
                          dur="4s"
                          repeatCount="indefinite"
                        />
                      </path>
                      <text
                        x="200"
                        y="255"
                        textAnchor="middle"
                        fill="white"
                        fontSize="16"
                        fontFamily="'JetBrains Mono', monospace"
                        style={{ pointerEvents: "none" }}
                      >
                        Crank clockwise
                      </text>
                    </g>
                  </g>
                )}
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CameraPage;
